import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import {
  FaBookOpen,
  FaHeart,
  FaOm,
  FaMoon,
  FaUsers,
  FaBell,
  FaPlus,
  FaTrash,
  FaSave,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaInfoCircle,
  FaList
} from "react-icons/fa";
import "../styles/ManageAbout.css";

const API = API_ENDPOINTS.ABOUT;
const UPLOAD_API = API_ENDPOINTS.UPLOAD;

const ManageAbout = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  /* ================= STATES ================= */
  const [aboutData, setAboutData] = useState([]);
  const [ghoshala, setGhoshala] = useState({ title: "", description: "", image: "", points: [] });
  const [rituals, setRituals] = useState([]);
  const [amavasya, setAmavasya] = useState({ title: "", description: "", image: "", points: [] });
  const [dhuni, setDhuni] = useState({ title: "", description: "", image: "", points: [] });
  const [team, setTeam] = useState([]);
  const [kumbabishekam, setKumbabishekam] = useState({ title: "", description: "", date: "", footer: "" });

  // UI Redesign Tab State
  const [activeTab, setActiveTab] = useState("about"); // "about" | "ghoshala" | "rituals" | "amavasya" | "dhuni" | "team" | "kumbabishekam"

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API);
        const d = res.data.data || {};

        setAboutData(d.aboutData || []);
        setGhoshala(d.ghoshala || { title: "", description: "", image: "", points: [] });
        setRituals(d.rituals || []);
        setAmavasya(d.amavasya || { title: "", description: "", image: "", points: [] });
        setDhuni(d.dhuni || { title: "", description: "", image: "", points: [] });
        setTeam(d.team || []);
        setKumbabishekam(d.kumbabishekam || { title: "", description: "", date: "", footer: "" });
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  /* ================= IMAGE UPLOAD ================= */
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("image", file);

    const res = await axios.post(UPLOAD_API, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return res.data.imageUrl;
  };

  /* ================= ABOUT SECTIONS ================= */
  const handleAboutImage = async (i, file) => {
    try {
      const url = await uploadImage(file);
      const updated = [...aboutData];
      updated[i].img = url;
      setAboutData(updated);
    } catch (e) {
      console.log(e);
      alert("Image upload failed");
    }
  };

  const updateAbout = (i, field, value) => {
    const updated = [...aboutData];
    updated[i][field] = value;
    setAboutData(updated);
  };

  const addAbout = () => {
    setAboutData([...aboutData, { title: "", text: "", img: "", reverse: false }]);
  };

  const deleteAbout = (i) => {
    if (window.confirm("Delete this about section?")) {
      setAboutData(aboutData.filter((_, index) => index !== i));
    }
  };

  /* ================= GHOSHALA ================= */
  const handleGhoshalaImage = async (file) => {
    try {
      const url = await uploadImage(file);
      setGhoshala({ ...ghoshala, image: url });
    } catch (e) {
      console.log(e);
      alert("Image upload failed");
    }
  };

  const updateGhoshalaPoint = (i, value) => {
    const updated = [...ghoshala.points];
    updated[i] = value;
    setGhoshala({ ...ghoshala, points: updated });
  };

  const addGhoshalaPoint = () => {
    setGhoshala({ ...ghoshala, points: [...(ghoshala.points || []), ""] });
  };

  const deleteGhoshalaPoint = (i) => {
    setGhoshala({
      ...ghoshala,
      points: ghoshala.points.filter((_, index) => index !== i),
    });
  };

  /* ================= RITUALS ================= */
  const handleRitualImage = async (i, file) => {
    try {
      const url = await uploadImage(file);
      const updated = [...rituals];
      updated[i].img = url;
      setRituals(updated);
    } catch (e) {
      console.log(e);
      alert("Image upload failed");
    }
  };

  const updateRitual = (i, field, value) => {
    const updated = [...rituals];
    updated[i][field] = value;
    setRituals(updated);
  };

  const addRitual = () => {
    setRituals([...rituals, { title: "", desc: "", img: "" }]);
  };

  const deleteRitual = (i) => {
    if (window.confirm("Delete this daily ritual?")) {
      setRituals(rituals.filter((_, index) => index !== i));
    }
  };

  /* ================= AMAVASYA ================= */
  const handleAmavasyaImage = async (file) => {
    try {
      const url = await uploadImage(file);
      setAmavasya({ ...amavasya, image: url });
    } catch (e) {
      console.log(e);
      alert("Image upload failed");
    }
  };

  const updateAmavasyaPoint = (i, value) => {
    const updated = [...amavasya.points];
    updated[i] = value;
    setAmavasya({ ...amavasya, points: updated });
  };

  const addAmavasyaPoint = () => {
    setAmavasya({ ...amavasya, points: [...(amavasya.points || []), ""] });
  };

  const deleteAmavasyaPoint = (i) => {
    setAmavasya({
      ...amavasya,
      points: amavasya.points.filter((_, index) => index !== i),
    });
  };

  /* ================= DHUNI ================= */
  const handleDhuniImage = async (file) => {
    try {
      const url = await uploadImage(file);
      setDhuni({ ...dhuni, image: url });
    } catch (e) {
      console.log(e);
      alert("Image upload failed");
    }
  };

  const updateDhuniPoint = (i, value) => {
    const updated = [...dhuni.points];
    updated[i] = value;
    setDhuni({ ...dhuni, points: updated });
  };

  const addDhuniPoint = () => {
    setDhuni({ ...dhuni, points: [...(dhuni.points || []), ""] });
  };

  const deleteDhuniPoint = (i) => {
    setDhuni({
      ...dhuni,
      points: dhuni.points.filter((_, index) => index !== i),
    });
  };

  /* ================= TEAM ================= */
  const handleTeamImage = async (i, file) => {
    try {
      const url = await uploadImage(file);
      const updated = [...team];
      updated[i].img = url;
      setTeam(updated);
    } catch (e) {
      console.log(e);
      alert("Image upload failed");
    }
  };

  const updateTeam = (i, field, value) => {
    const updated = [...team];
    updated[i][field] = value;
    setTeam(updated);
  };

  const addTeam = () => {
    setTeam([...team, { name: "", role: "", text: "", fullText: "", img: "", reverse: false }]);
  };

  const deleteTeam = (i) => {
    if (window.confirm("Delete this team member?")) {
      setTeam(team.filter((_, index) => index !== i));
    }
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);

      const finalData = {
        aboutData,
        ghoshala,
        rituals,
        amavasya,
        dhuni,
        team,
        kumbabishekam,
      };

      await axios.post(API, finalData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      alert("🎉 About Page Saved Successfully!");
    } catch (err) {
      console.log(err);
      alert("❌ Failed To Save About Page");
    } finally {
      setSaving(false);
    }
  };

  /* ================= RENDER LOADING ================= */
  if (loading) {
    return (
      <div className="about-loading">
        <div className="spinner"></div>
        <h2>Loading About Configuration...</h2>
        <p>Syncing database schemas</p>
      </div>
    );
  }

  return (
    <div className="manage-about-container">
      {/* BANNER SECTION */}
      <div className="about-banner">
        <div className="banner-content">
          <h1>🏛️ Manage About Page</h1>
          <p>Configure history pages, Ghoshala services, daily temple rituals, Amavasya programs, Dhuni details, and team profiles.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-pill">
            <FaBookOpen className="icon" />
            <span><strong>{aboutData.length}</strong> Sections</span>
          </div>
          <div className="stat-pill">
            <FaOm className="icon" />
            <span><strong>{rituals.length}</strong> Rituals</span>
          </div>
          <div className="stat-pill">
            <FaUsers className="icon" />
            <span><strong>{team.length}</strong> Members</span>
          </div>
        </div>
      </div>

      {/* TABS & DASHBOARD CONTROLS */}
      <div className="dashboard-controls">
        <div className="tabs-wrapper">
          <button 
            className={`tab-btn ${activeTab === "about" ? "active" : ""}`}
            onClick={() => setActiveTab("about")}
          >
            <FaBookOpen /> About Cards
          </button>
          <button 
            className={`tab-btn ${activeTab === "ghoshala" ? "active" : ""}`}
            onClick={() => setActiveTab("ghoshala")}
          >
            <FaHeart /> Ghoshala
          </button>
          <button 
            className={`tab-btn ${activeTab === "rituals" ? "active" : ""}`}
            onClick={() => setActiveTab("rituals")}
          >
            <FaOm /> Daily Rituals
          </button>
          <button 
            className={`tab-btn ${activeTab === "amavasya" ? "active" : ""}`}
            onClick={() => setActiveTab("amavasya")}
          >
            <FaMoon /> Amavasya
          </button>
          <button 
            className={`tab-btn ${activeTab === "dhuni" ? "active" : ""}`}
            onClick={() => setActiveTab("dhuni")}
          >
            <FaMoon style={{ transform: "rotate(90deg)" }} /> Dhuni
          </button>
          <button 
            className={`tab-btn ${activeTab === "team" ? "active" : ""}`}
            onClick={() => setActiveTab("team")}
          >
            <FaUsers /> Team Members
          </button>
          <button 
            className={`tab-btn ${activeTab === "kumbabishekam" ? "active" : ""}`}
            onClick={() => setActiveTab("kumbabishekam")}
          >
            <FaBell /> Kumbabishekam
          </button>
        </div>

        <div className="quick-action-buttons">
          {activeTab === "about" && (
            <button className="action-btn add-btn-primary" onClick={addAbout}>
              <FaPlus /> Add Section
            </button>
          )}
          {activeTab === "rituals" && (
            <button className="action-btn add-btn-primary" onClick={addRitual}>
              <FaPlus /> Add Ritual
            </button>
          )}
          {activeTab === "team" && (
            <button className="action-btn add-btn-primary" onClick={addTeam}>
              <FaPlus /> Add Member
            </button>
          )}
          <button 
            className={`action-btn save-btn-primary ${saving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="spinner-small"></div> Saving...
              </>
            ) : saveSuccess ? (
              <>
                <FaCheckCircle /> Saved!
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* PANEL CONTAINER */}
      <div className="panel-container">
        
        {/* TAB PANEL: ABOUT CARDS */}
        {activeTab === "about" && (
          <div className="directory-section fade-in">
            {aboutData.length === 0 ? (
              <div className="empty-state">
                <FaBookOpen className="empty-icon" />
                <h3>No About Cards</h3>
                <p>Create informative detail blocks that show the history and vision of the temple.</p>
                <button className="action-btn add-btn-primary" onClick={addAbout}>
                  <FaPlus /> Add First Card
                </button>
              </div>
            ) : (
              <div className="cards-grid">
                {aboutData.map((item, i) => (
                  <div className="content-card" key={i}>
                    <div className="card-header-actions">
                      <span className="card-number">About Section #{i + 1}</span>
                      <button className="card-delete-btn" onClick={() => deleteAbout(i)}>
                        <FaTrash /> Delete
                      </button>
                    </div>

                    <div className="card-body">
                      <div className="form-group-compact">
                        <label>Section Title</label>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => updateAbout(i, "title", e.target.value)}
                          placeholder="e.g. Temple Inception & History"
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>Content Text</label>
                        <textarea
                          value={item.text}
                          onChange={(e) => updateAbout(i, "text", e.target.value)}
                          placeholder="Enter history details, mission text, or descriptions..."
                          rows={4}
                        />
                      </div>

                      <div className="form-row align-center">
                        <label className="custom-checkbox-wrapper">
                          <input
                            type="checkbox"
                            checked={item.reverse}
                            onChange={(e) => updateAbout(i, "reverse", e.target.checked)}
                          />
                          <span className="checkbox-box"></span>
                          Reverse Layout (Image on Left)
                        </label>
                        
                        <div className="form-group-compact flex-grow">
                          <label>Upload Section Image</label>
                          <label className="custom-file-upload">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleAboutImage(i, e.target.files[0])}
                            />
                            <FaCloudUploadAlt className="icon" />
                            <span>Choose Image</span>
                          </label>
                        </div>
                      </div>

                      {item.img && (
                        <div className="image-preview-card">
                          <img src={item.img} alt="Section Preview" className="preview-img-block" />
                          <div className="image-badge">Live Image</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB PANEL: GHOSHALA */}
        {activeTab === "ghoshala" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaHeart className="title-icon" style={{ color: "#ef4444" }} /> Ghoshala (Cow Shelter) Details</h3>
              <p>Promote cow protection services, feed sponsorships, and daily shelter routines.</p>
            </div>

            <div className="panel-card-body-layout">
              <div className="left-form-panel">
                <div className="form-group">
                  <label>Section Title</label>
                  <input
                    type="text"
                    value={ghoshala.title}
                    onChange={(e) => setGhoshala({ ...ghoshala, title: e.target.value })}
                    placeholder="e.g. Sri Sai Ghoshala Protection"
                  />
                </div>

                <div className="form-group">
                  <label>Description / Details</label>
                  <textarea
                    value={ghoshala.description}
                    onChange={(e) => setGhoshala({ ...ghoshala, description: e.target.value })}
                    placeholder="Describe how the shelter is maintained and sponsor opportunities..."
                    rows={4}
                  />
                </div>

                <div className="form-group">
                  <label>Shelter Image</label>
                  <label className="custom-file-upload wide-uploader">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleGhoshalaImage(e.target.files[0])}
                    />
                    <FaCloudUploadAlt className="icon" />
                    <span>Upload New Ghoshala Image</span>
                  </label>
                </div>
              </div>

              <div className="right-preview-panel">
                {ghoshala.image ? (
                  <div className="image-preview-card full-height">
                    <img src={ghoshala.image} alt="Ghoshala Preview" className="preview-img-block object-cover" />
                  </div>
                ) : (
                  <div className="preview-placeholder">
                    <FaInfoCircle className="icon" />
                    <span>No Image Uploaded Yet</span>
                  </div>
                )}
              </div>
            </div>

            <div className="points-management-box">
              <div className="points-header">
                <h4><FaList className="points-icon" /> Highlight Bullet Points</h4>
                <button className="action-btn add-btn-primary" onClick={addGhoshalaPoint}>
                  <FaPlus /> Add Bullet Point
                </button>
              </div>

              {(!ghoshala.points || ghoshala.points.length === 0) ? (
                <div className="empty-points-state">No highlights listed. Add points to show key details.</div>
              ) : (
                <div className="points-inputs-grid">
                  {ghoshala.points.map((p, i) => (
                    <div className="point-item-card" key={i}>
                      <span className="point-label">Point #{i + 1}</span>
                      <div className="point-input-wrapper">
                        <input
                          type="text"
                          value={p}
                          onChange={(e) => updateGhoshalaPoint(i, e.target.value)}
                          placeholder="e.g. 50+ Holy cows sheltered..."
                        />
                        <button className="point-delete-btn" onClick={() => deleteGhoshalaPoint(i)}>
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB PANEL: DAILY RITUALS */}
        {activeTab === "rituals" && (
          <div className="directory-section fade-in">
            {rituals.length === 0 ? (
              <div className="empty-state">
                <FaOm className="empty-icon" />
                <h3>No Rituals Listed</h3>
                <p>Add the daily aartis, abhishekams, or bhajans performed in the temple.</p>
                <button className="action-btn add-btn-primary" onClick={addRitual}>
                  <FaPlus /> Add First Ritual
                </button>
              </div>
            ) : (
              <div className="cards-grid">
                {rituals.map((r, i) => (
                  <div className="content-card" key={i}>
                    <div className="card-header-actions">
                      <span className="card-number">Daily Ritual #{i + 1}</span>
                      <button className="card-delete-btn" onClick={() => deleteRitual(i)}>
                        <FaTrash /> Delete
                      </button>
                    </div>

                    <div className="card-body">
                      <div className="form-group-compact">
                        <label>Ritual Name / Title</label>
                        <input
                          type="text"
                          value={r.title}
                          onChange={(e) => updateRitual(i, "title", e.target.value)}
                          placeholder="e.g. Kakad Aarti"
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>Description / Timings</label>
                        <textarea
                          value={r.desc}
                          onChange={(e) => updateRitual(i, "desc", e.target.value)}
                          placeholder="Provide timing details, significance of the worship..."
                          rows={3}
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>Ritual Showcase Image</label>
                        <label className="custom-file-upload">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleRitualImage(i, e.target.files[0])}
                          />
                          <FaCloudUploadAlt className="icon" />
                          <span>Choose Image</span>
                        </label>
                      </div>

                      {r.img && (
                        <div className="image-preview-card">
                          <img src={r.img} alt="Ritual Preview" className="preview-img-block" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB PANEL: AMAVASYA */}
        {activeTab === "amavasya" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaMoon className="title-icon" style={{ color: "#0f2d5c" }} /> Amavasya (New Moon Pooja)</h3>
              <p>Manage details regarding the highly auspicious Amavasya special offerings and events.</p>
            </div>

            <div className="panel-card-body-layout">
              <div className="left-form-panel">
                <div className="form-group">
                  <label>Section Title</label>
                  <input
                    type="text"
                    value={amavasya.title}
                    onChange={(e) => setAmavasya({ ...amavasya, title: e.target.value })}
                    placeholder="e.g. Special Amavasya Pooja Services"
                  />
                </div>

                <div className="form-group">
                  <label>Description / Schedule Info</label>
                  <textarea
                    value={amavasya.description}
                    onChange={(e) => setAmavasya({ ...amavasya, description: e.target.value })}
                    placeholder="Provide details about timings, special ticket entry, prasadam..."
                    rows={4}
                  />
                </div>

                <div className="form-group">
                  <label>Pooja Image</label>
                  <label className="custom-file-upload wide-uploader">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleAmavasyaImage(e.target.files[0])}
                    />
                    <FaCloudUploadAlt className="icon" />
                    <span>Upload New Amavasya Image</span>
                  </label>
                </div>
              </div>

              <div className="right-preview-panel">
                {amavasya.image ? (
                  <div className="image-preview-card full-height">
                    <img src={amavasya.image} alt="Amavasya Preview" className="preview-img-block object-cover" />
                  </div>
                ) : (
                  <div className="preview-placeholder">
                    <FaInfoCircle className="icon" />
                    <span>No Image Uploaded Yet</span>
                  </div>
                )}
              </div>
            </div>

            <div className="points-management-box">
              <div className="points-header">
                <h4><FaList className="points-icon" /> Highlights / Program Steps</h4>
                <button className="action-btn add-btn-primary" onClick={addAmavasyaPoint}>
                  <FaPlus /> Add Bullet Point
                </button>
              </div>

              {(!amavasya.points || amavasya.points.length === 0) ? (
                <div className="empty-points-state">No program items listed. Add points to show key program highlights.</div>
              ) : (
                <div className="points-inputs-grid">
                  {amavasya.points.map((p, i) => (
                    <div className="point-item-card" key={i}>
                      <span className="point-label">Item #{i + 1}</span>
                      <div className="point-input-wrapper">
                        <input
                          type="text"
                          value={p}
                          onChange={(e) => updateAmavasyaPoint(i, e.target.value)}
                          placeholder="e.g. Mahamangal Aarti at 7:00 PM..."
                        />
                        <button className="point-delete-btn" onClick={() => deleteAmavasyaPoint(i)}>
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB PANEL: DHUNI */}
        {activeTab === "dhuni" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaMoon className="title-icon" style={{ transform: "rotate(90deg)", color: "#f5c542" }} /> Sacred Dhuni (Eternal Fire) Details</h3>
              <p>Share details about the sacred dhuni wood sponsorship, history, and timings.</p>
            </div>

            <div className="panel-card-body-layout">
              <div className="left-form-panel">
                <div className="form-group">
                  <label>Section Title</label>
                  <input
                    type="text"
                    value={dhuni.title}
                    onChange={(e) => setDhuni({ ...dhuni, title: e.target.value })}
                    placeholder="e.g. Sacred Dwarkamai Dhuni Pooja"
                  />
                </div>

                <div className="form-group">
                  <label>History & Importance Description</label>
                  <textarea
                    value={dhuni.description}
                    onChange={(e) => setDhuni({ ...dhuni, description: e.target.value })}
                    placeholder="Explain the importance of sacred ash (Udi) and rituals..."
                    rows={4}
                  />
                </div>

                <div className="form-group">
                  <label>Dhuni Showcase Image</label>
                  <label className="custom-file-upload wide-uploader">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleDhuniImage(e.target.files[0])}
                    />
                    <FaCloudUploadAlt className="icon" />
                    <span>Upload New Dhuni Image</span>
                  </label>
                </div>
              </div>

              <div className="right-preview-panel">
                {dhuni.image ? (
                  <div className="image-preview-card full-height">
                    <img src={dhuni.image} alt="Dhuni Preview" className="preview-img-block object-cover" />
                  </div>
                ) : (
                  <div className="preview-placeholder">
                    <FaInfoCircle className="icon" />
                    <span>No Image Uploaded Yet</span>
                  </div>
                )}
              </div>
            </div>

            <div className="points-management-box">
              <div className="points-header">
                <h4><FaList className="points-icon" /> Timings & Offerings</h4>
                <button className="action-btn add-btn-primary" onClick={addDhuniPoint}>
                  <FaPlus /> Add Bullet Point
                </button>
              </div>

              {(!dhuni.points || dhuni.points.length === 0) ? (
                <div className="empty-points-state">No points listed. Add items to show timings or offerings details.</div>
              ) : (
                <div className="points-inputs-grid">
                  {dhuni.points.map((p, i) => (
                    <div className="point-item-card" key={i}>
                      <span className="point-label">Point #{i + 1}</span>
                      <div className="point-input-wrapper">
                        <input
                          type="text"
                          value={p}
                          onChange={(e) => updateDhuniPoint(i, e.target.value)}
                          placeholder="e.g. Dry coconut offerings allowed..."
                        />
                        <button className="point-delete-btn" onClick={() => deleteDhuniPoint(i)}>
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB PANEL: TEAM */}
        {activeTab === "team" && (
          <div className="directory-section fade-in">
            {team.length === 0 ? (
              <div className="empty-state">
                <FaUsers className="empty-icon" />
                <h3>No Members Registered</h3>
                <p>Create profiles for the temple trustees, chief priests, or executive members.</p>
                <button className="action-btn add-btn-primary" onClick={addTeam}>
                  <FaPlus /> Add Member Now
                </button>
              </div>
            ) : (
              <div className="cards-grid">
                {team.map((t, i) => (
                  <div className="content-card" key={i}>
                    <div className="card-header-actions">
                      <span className="card-number">Trustee/Priest #{i + 1}</span>
                      <button className="card-delete-btn" onClick={() => deleteTeam(i)}>
                        <FaTrash /> Delete
                      </button>
                    </div>

                    <div className="card-body">
                      <div className="form-row">
                        <div className="form-group-compact flex-grow">
                          <label>Full Name</label>
                          <input
                            type="text"
                            value={t.name}
                            onChange={(e) => updateTeam(i, "name", e.target.value)}
                            placeholder="e.g. Sri R. Nithyanandan"
                          />
                        </div>
                        <div className="form-group-compact flex-grow">
                          <label>Role / Designation</label>
                          <input
                            type="text"
                            value={t.role}
                            onChange={(e) => updateTeam(i, "role", e.target.value)}
                            placeholder="e.g. Founder & Chief Trustee"
                          />
                        </div>
                      </div>

                      <div className="form-group-compact">
                        <label>Short Bio (Card Summary)</label>
                        <textarea
                          value={t.text}
                          onChange={(e) => updateTeam(i, "text", e.target.value)}
                          placeholder="Enter a brief summary (1-2 sentences)..."
                          rows={2}
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>Full Bio / Detailed Description</label>
                        <textarea
                          value={t.fullText}
                          onChange={(e) => updateTeam(i, "fullText", e.target.value)}
                          placeholder="Enter detailed description of contributions or responsibilities..."
                          rows={4}
                        />
                      </div>

                      <div className="form-row align-center">
                        <label className="custom-checkbox-wrapper">
                          <input
                            type="checkbox"
                            checked={t.reverse}
                            onChange={(e) => updateTeam(i, "reverse", e.target.checked)}
                          />
                          <span className="checkbox-box"></span>
                          Reverse Layout (Profile on Left)
                        </label>
                        
                        <div className="form-group-compact flex-grow">
                          <label>Profile Picture</label>
                          <label className="custom-file-upload">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleTeamImage(i, e.target.files[0])}
                            />
                            <FaCloudUploadAlt className="icon" />
                            <span>Choose Photo</span>
                          </label>
                        </div>
                      </div>

                      {t.img && (
                        <div className="image-preview-card rounded-circle-wrapper">
                          <img src={t.img} alt={t.name} className="preview-img-block circle-img" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB PANEL: KUMBABISHEKAM */}
        {activeTab === "kumbabishekam" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaBell className="title-icon" style={{ color: "#f5c542" }} /> Kumbabishekam (Consecration Ceremony) Details</h3>
              <p>Manage records or upcoming announcements regarding the sacred Kumbabishekam rituals.</p>
            </div>

            <div className="panel-card-body">
              <div className="form-group">
                <label>Ceremony Title</label>
                <input
                  type="text"
                  value={kumbabishekam.title}
                  onChange={(e) => setKumbabishekam({ ...kumbabishekam, title: e.target.value })}
                  placeholder="e.g. Mahakumbabishekam Mahotsavam"
                />
              </div>

              <div className="form-group">
                <label>Event Date / Timings Text</label>
                <input
                  type="text"
                  value={kumbabishekam.date}
                  onChange={(e) => setKumbabishekam({ ...kumbabishekam, date: e.target.value })}
                  placeholder="e.g. July 24, 2026 - July 28, 2026"
                />
              </div>

              <div className="form-group">
                <label>Ceremony Description / Schedule</label>
                <textarea
                  value={kumbabishekam.description}
                  onChange={(e) => setKumbabishekam({ ...kumbabishekam, description: e.target.value })}
                  placeholder="Describe the consecration procedures, priests participating, details..."
                  rows={5}
                />
              </div>

              <div className="form-group">
                <label>Footer / Inviter Details</label>
                <input
                  type="text"
                  value={kumbabishekam.footer}
                  onChange={(e) => setKumbabishekam({ ...kumbabishekam, footer: e.target.value })}
                  placeholder="e.g. All are cordially invited - Temple Trust Board"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* FLOATING SAVE BAR */}
      <div className="floating-save-bar">
        <div className="save-bar-info">
          <span>Unsaved changes to the About configuration will be lost unless saved.</span>
        </div>
        <button 
          className={`action-btn save-btn-primary ${saving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="spinner-small"></div> Saving...
            </>
          ) : saveSuccess ? (
            <>
              <FaCheckCircle /> Saved Successfully!
            </>
          ) : (
            <>
              <FaSave /> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ManageAbout;
